import React, { forwardRef, useCallback, useRef } from 'react'
import { NavLinkProps, NavLink } from 'react-router-dom'
import { IconForm, IconName, IconColor } from '../Icon'
import styled, { css } from '../themes/styled'
import SectionLoader from '../Loader/SectionLoader'

export type ButtonColor = 'primary' | 'neutral'
export type ButtonForm = 'filled' | 'outlined' | 'text' | 'icon'

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
    color?: ButtonColor
    form?: ButtonForm
    icon?: IconName
    iconForm?: IconForm
    iconFillColor?: IconColor
    isDisabled?: boolean
    isLoading?: boolean
    dataTestId?: string
}

export const withLinkWrapper: <T extends ButtonProps>(
    component: React.FC<T>,
) => React.FC<T & NavLinkProps> = Component =>
    function LinkWrapperHOC({ to, ...props }): JSX.Element {
        const isDisabled = props.isDisabled || props.isLoading
        const preventRedirect = useCallback(e => e.preventDefault(), [])
        const ref = useRef<HTMLButtonElement>(null)
        return (
            <NavLink
                to={to}
                // there is no `disabled` attribute for anchor tag,
                // so we need to prevent re-directing by this
                onClick={isDisabled ? preventRedirect : undefined}
                onFocus={() => ref.current && ref.current.focus()}
            >
                <Component ref={ref} {...(props as any)} />
            </NavLink>
        )
    }

// Form is a button keyword, updated the property name to buttonForm to avoid unexpected button behavior inside a form
interface StyledButtonContainerProps extends Omit<ButtonProps, 'form'> {
    buttonForm?: ButtonForm
}

export const styledDisabled = css<StyledButtonContainerProps>`
    ${({ theme, buttonForm }) =>
        buttonForm &&
        {
            filled: {
                background: theme.colors.surface.disable,
            },
            outlined: {
                background: 'none',
                border: `var(--border-setting) ${theme.colors.border.disable}`,
            },
            text: {
                background: 'none',
            },
            icon: {
                background: 'none',
            },
        }[buttonForm]}
    .button-label, .sectionLoader-label {
        ${({ isLoading, isDisabled }) => ({ cursor: isLoading ? 'default' : isDisabled ? 'not-allowed' : 'pointer' })};
        ${({ theme, buttonForm }) =>
            buttonForm &&
            {
                filled: {
                    color: theme.colors.text.disable,
                },
                outlined: {
                    color: theme.colors.text.disable,
                },
                text: {
                    color: theme.colors.text.disable,
                },
                icon: {
                    color: theme.colors.icon.disable,
                },
            }[buttonForm]}
    }
    .button-icon,
    .sectionLoader-icon {
        ${({ isLoading, isDisabled }) => ({ cursor: isLoading ? 'default' : isDisabled ? 'not-allowed' : 'pointer' })};
        ${({ theme, buttonForm }) =>
            buttonForm &&
            {
                filled: {
                    color: theme.colors.icon.disable,
                },
                outlined: {
                    color: theme.colors.icon.disable,
                },
                text: {
                    color: theme.colors.icon.disable,
                },
                icon: {
                    color: theme.colors.icon.disable,
                },
            }[buttonForm]}
    }
`

const styledFormFilled = css<StyledButtonContainerProps>`
    // Background
    ${({ theme, color }) => ({
        background: color === 'primary' ? theme.colors.surface.action : theme.colors.surface.neutralAction,
    })}
    // Label and icon color
    .button-label, .sectionLoader-label {
        ${({ theme }) => ({
            color: theme.colors.text.actionInverse,
        })}
    }
    .button-icon,
    .sectionLoader-icon {
        ${({ theme }) => ({
            color: theme.colors.icon.actionInverse,
        })}
    }
    ${({ isLoading, isDisabled }) =>
        !(isLoading || isDisabled) &&
        css<ButtonProps>`
            // Hover style
            &:hover {
                ${({ theme, color }) => ({
                    background:
                        color === 'primary'
                            ? theme.colors.surface.actionHover
                            : theme.colors.surface.neutralActionHover,
                })}
            }
            // active style
            &:active .button-label {
                ${({ theme }) => ({
                    color: theme.colors.text.actionInverse,
                })}
            }
            &:active .button-icon {
                ${({ theme }) => ({
                    color: theme.colors.text.actionInverse,
                })}
            }
            &:active {
                ${({ theme, color }) => ({
                    background:
                        color === 'primary'
                            ? theme.colors.surface.actionActive
                            : theme.colors.surface.neutralActionActive,
                })}
            }
            /*
            * Focus style
            * Used focus-visible instead of focus
            * https://developer.mozilla.org/en-US/docs/Web/CSS/:focus-visible
            */
            &:focus-visible {
                ${({ theme, color }) => ({
                    outline: `var(--outline-setting) ${
                        color === 'primary' ? theme.colors.border.actionFocus : theme.colors.border.neutralActionFocus
                    }`,
                })}
            }
        `}
`

const styledFormOutlined = css<StyledButtonContainerProps>`
    // Background
    ${({ theme, color }) => ({
        background: 'none',
        border: `var(--border-setting) ${
            color == 'primary' ? theme.colors.border.action : theme.colors.border.neutralAction
        }`,
    })}
    // Label and icon color
    .button-label, .sectionLoader-label {
        ${({ theme, color }) => ({
            color: color === 'primary' ? theme.colors.text.action : theme.colors.text.neutralAction,
        })}
    }
    .button-icon,
    .sectionLoader-icon {
        ${({ theme, color }) => ({
            color: color === 'primary' ? theme.colors.icon.action : theme.colors.icon.neutralAction,
        })}
    }
    ${({ isLoading, isDisabled }) =>
        !(isLoading || isDisabled) &&
        css<ButtonProps>`
            // Hover style
            &:hover {
                ${({ theme, color }) => ({
                    background:
                        color === 'primary'
                            ? theme.colors.surface.actionPastelHover
                            : theme.colors.surface.neutralActionPastelHover,
                })}
            }
            // active style
            &.isActivated,
            &:active {
                .button-label {
                    ${({ theme, color }) => ({
                        color:
                            color === 'primary'
                                ? theme.colors.text.actionActive
                                : theme.colors.text.neutralActionActive,
                    })}
                }
                .button-icon {
                    ${({ theme, color }) => ({
                        color:
                            color === 'primary'
                                ? theme.colors.icon.actionActive
                                : theme.colors.icon.neutralActionActive,
                    })}
                }
                background-color: ${({ theme, color }) =>
                    color === 'primary'
                        ? theme.colors.surface.actionPastelHover
                        : theme.colors.surface.neutralActionPastelHover};
                border-color: ${({ theme, color }) =>
                    color === 'primary' ? theme.colors.border.actionActive : theme.colors.border.neutralActionActive};
            }
            /*
            * Focus style
            * Used focus-visible instead of focus
            * https://developer.mozilla.org/en-US/docs/Web/CSS/:focus-visible
            */
            &:focus-visible {
                ${({ theme, color }) => ({
                    outline: `var(--outline-setting) ${
                        color === 'primary' ? theme.colors.border.actionFocus : theme.colors.border.neutralActionFocus
                    }`,
                })}
            }
        `}
`

const styledFormTextIcon = css<StyledButtonContainerProps>`
    // Background
    background: none;
    // Label and icon color
    .button-label,
    .sectionLoader-label {
        ${({ theme, color }) => ({
            color: color === 'primary' ? theme.colors.text.action : theme.colors.text.neutralAction,
        })}
    }
    .button-icon,
    .sectionLoader-icon {
        ${({ theme, color }) => ({
            color: color === 'primary' ? theme.colors.icon.action : theme.colors.icon.neutralAction,
        })}
    }
    ${({ isLoading, isDisabled }) =>
        !(isLoading || isDisabled) &&
        css<ButtonProps>`
            // Hover style
            &:hover {
                ${({ theme, color }) => ({
                    background:
                        color === 'primary'
                            ? theme.colors.surface.actionPastelHover
                            : theme.colors.surface.neutralActionPastelHover,
                })}
            }
            // active style
            &:active .button-label {
                ${({ theme, color }) => ({
                    color: color === 'primary' ? theme.colors.text.actionActive : theme.colors.text.neutralActionActive,
                })}
            }
            &:active .button-icon {
                ${({ theme, color }) => ({
                    color: color === 'primary' ? theme.colors.icon.actionActive : theme.colors.icon.neutralActionActive,
                })}
            }
            /*
            * Focus style
            * Used focus-visible instead of focus
            * https://developer.mozilla.org/en-US/docs/Web/CSS/:focus-visible
            */
            &:focus-visible {
                ${({ theme, color }) => ({
                    outline: `var(--outline-setting) ${
                        color === 'primary' ? theme.colors.border.actionFocus : theme.colors.border.neutralActionFocus
                    }`,
                })}
            }
        `}
`

const StyledButtonContainer = styled.button<StyledButtonContainerProps>`
    // Border and outline variables
    --border-setting: 1.5px solid;
    --outline-setting: 3px solid;
    // Common button style
    border: 0;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    font-family: inherit;
    box-sizing: border-box;
    ${({ isLoading, isDisabled }) => ({ cursor: isLoading ? 'default' : isDisabled ? 'not-allowed' : 'pointer' })}
    // Remove focus and use focus-visible instead
    &:focus {
        outline: 0;
    }
    ${({ buttonForm }) => {
        switch (buttonForm) {
            case 'filled':
                return styledFormFilled
            case 'outlined':
                return styledFormOutlined
            case 'text':
            case 'icon':
                return styledFormTextIcon
            default:
                return
        }
    }}
    ${({ isDisabled }) =>
        isDisabled &&
        css`
            ${styledDisabled}
        `}
`

export const ButtonContainer = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ form, isLoading, isDisabled, color, children, dataTestId, ...props }, ref) => {
        return (
            <StyledButtonContainer
                ref={ref}
                buttonForm={form}
                isDisabled={isLoading ? false : isDisabled}
                isLoading={isLoading}
                color={color}
                data-testid={dataTestId}
                {...props}
                onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                    if (isDisabled || isLoading) e.preventDefault()
                    if ((e.metaKey || e.ctrlKey) && !isDisabled) return // Allow command-click and control-click to open new tab
                    if (!isDisabled && !isLoading && props.onClick) props.onClick(e)
                }}
                onContextMenu={(e: React.MouseEvent<HTMLButtonElement>): void => {
                    if (isDisabled || isLoading) {
                        e.preventDefault()
                    } else if (props.onContextMenu) {
                        props.onContextMenu(e)
                    }
                }}
            >
                {children}
            </StyledButtonContainer>
        )
    },
)

ButtonContainer.displayName = 'ButtonContainer'

export const Loader = styled(SectionLoader)`
    padding: 0;
`
